import { Text } from '@components/ui';
import AreaOfExpertise from '@features/profile/area-of-expertise/area-of-expertise';
import ServiceArea from '@features/profile/service-area/service-area';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

const steps = [
  { id: 'areas', labelKey: 'nav.serviceAreas' },
  { id: 'expertise', labelKey: 'nav.areasOfExpertise' },
] as const;

type WorkProfileStep = (typeof steps)[number]['id'];

const WorkProfile = (): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('step');
  const step: WorkProfileStep =
    requested === 'expertise' || requested === 'level' ? 'expertise' : 'areas';

  const selectStep = (next: WorkProfileStep): void => {
    const params = new URLSearchParams(searchParams);
    params.set('step', next);
    setSearchParams(params, { replace: true });
  };

  const panel = (current: WorkProfileStep): JSX.Element => {
    switch (current) {
      case 'areas':
        return <ServiceArea embedded />;
      case 'expertise':
        return <AreaOfExpertise embedded />;
      default: {
        const unreachable: never = current;
        return unreachable;
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        {steps.map((item) => {
          const selected = item.id === step;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={() => selectStep(item.id)}
              aria-current={selected ? 'step' : undefined}
              sx={{
                border: 1,
                borderColor: selected ? 'text.primary' : 'divider',
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: 600,
                fontSize: 14,
                px: 1.75,
                py: 1,
                borderRadius: 999,
                color: selected ? 'text.primary' : 'text.secondary',
                bgcolor: selected ? 'action.selected' : 'background.paper',
              }}
            >
              {t(item.labelKey)}
            </Box>
          );
        })}
      </Box>
      <Text variant="body" tone="muted" sx={{ display: 'block', mb: 3 }}>
        {t('nav.workProfileHint')}
      </Text>
      {panel(step)}
    </Box>
  );
};

export default WorkProfile;
